<script setup lang="ts">
import { ref } from "vue";
import Lucide from "../../base-components/Lucide";
import { useMobileMenuStore } from "../../stores/mobile-menu";
import Breadcrumb from "../../base-components/Breadcrumb";
import { Menu, Popover } from "../../base-components/Headless";
import _ from "lodash";
import { userUserStore } from "../../stores/user";
import { getAuth, signOut } from "firebase/auth";
import router from "../../router";
import DarkModeSwitcher from "../DarkModeSwitcher";

const ambiente = import.meta.env.VITE_ENV_NOMBRE;

const props = defineProps<{
  layout?: "side-menu" | "simple-menu" | "top-menu";
}>();

const userStore = userUserStore();
const mobileMenuStore = useMobileMenuStore();

const mailAMostrar = (mail: string) => {
  if (mail.includes(".fixed.uy")) {
    const mailSplit = mail.split("@");
    return mailSplit[0];
  } else {
    return mail;
  }
};
const logoutHandler = () => {
  const auth = getAuth();
  signOut(auth).then(() => {
    userStore.signOut();
    router.push("/login");
  });
};
</script>

<template>
  <div
    :class="[
      'h-[56px] md:h-[52px] lg:h-[65px] z-[51] border-b border-white/[0.08] mt-0 -mx-3 sm:-mx-8 md:-mx-0 px-3 md:border-b-0 relative sm:px-8 md:px-6 lg:px-10 md:pt-4 lg:pt-10 md:pb-2 lg:pb-0 md:bg-gradient-to-b md:from-slate-100 md:to-transparent dark:md:from-darkmode-700',
      props.layout == 'top-menu' && 'dark:md:from-darkmode-800',
      'before:content-[\'\'] before:absolute before:h-[52px] before:lg:h-[65px] before:inset-0 before:top-0 before:mx-5 before:lg:mx-7 before:bg-primary/30 before:mt-2 before:lg:mt-3 before:rounded-lg before:lg:rounded-xl before:hidden before:md:block before:dark:bg-darkmode-600/30',
      'after:content-[\'\'] after:absolute after:inset-0 after:h-[52px] after:lg:h-[65px] after:mx-3 after:bg-primary after:mt-3 after:lg:mt-5 after:rounded-lg after:lg:rounded-xl after:shadow-md after:hidden after:md:block after:dark:bg-darkmode-600'
    ]"
  >
    <div class="flex items-center h-full">
      <!-- Hamburger: móvil y tablet (< lg), siempre a la izquierda -->
      <div
        class="flex lg:hidden items-center mr-3 md:mr-4 shrink-0 order-first"
      >
        <button
          type="button"
          class="p-2 -m-2 rounded-lg text-white/85 hover:text-white hover:bg-white/10 transition"
          aria-label="Abrir menú"
          @click="mobileMenuStore.toggle"
        >
          <Lucide icon="BarChart2" class="w-6 h-6 transform -rotate-90" />
        </button>
      </div>

      <!-- Logo (oculto en móvil/tablet en top-menu) -->
      <div
        :class="[
          '-intro-x hidden md:flex',
          props.layout == 'side-menu' && 'xl:w-[110px]',
          props.layout == 'simple-menu' && 'xl:w-auto',
          props.layout == 'top-menu' && 'w-auto'
        ]"
      >
        <template v-if="ambiente == 'produccion' || ambiente == 'Desarrollo'">
          <!-- <img alt="FixedEstudios" class="w-28" src="/FixedEstudios.png" /> -->
        </template>
        <template v-else>
          <span
            :class="[
              'text-lg text-white',
              props.layout == 'side-menu' && 'hidden xl:block',
              props.layout == 'simple-menu' && 'hidden'
            ]"
          >
            {{ ambiente }}
          </span>
        </template>
      </div>

      <!-- Breadcrumb -->
      <Breadcrumb
        light
        :class="[
          'h-[40px] lg:h-[45px] md:ml-6 lg:ml-10 md:border-l border-white/[0.08] dark:border-white/[0.08] mr-auto -intro-x text-lg md:text-xl lg:text-2xl',
          props.layout != 'top-menu' && 'md:pl-6',
          props.layout == 'top-menu' && 'md:pl-10'
        ]"
      >
        {{ userStore.empresa?.nombreComercial }} - {{ userStore.empresa?.rut }}
      </Breadcrumb>

      <!-- Dark mode -->
      <!-- <div class="relative mr-3 intro-x sm:mr-6">
        <DarkModeSwitcher />
      </div> -->

      <!-- Notifications -->
      <Popover as="div" class="mr-4 intro-x sm:mr-6">
        <Popover.Button
          as="button"
          class="relative text-white/70 outline-none block before:content-[''] before:w-[8px] before:h-[8px] before:rounded-full before:absolute before:top-[-2px] before:right-0 before:bg-danger"
        >
          <Lucide icon="Bell" class="w-5 h-5 dark:text-slate-500" />
        </Popover.Button>

        <Popover.Panel as="div" class="w-[280px] sm:w-[350px] p-5 mt-2">
          <div class="mb-5 font-medium">Notificaciones</div>
          <p>No hay notificaciones pendientes.</p>
        </Popover.Panel>
      </Popover>

      <!-- Account menu (foto usuario) -->
      <Menu as="div">
        <Menu.Button
          as="button"
          class="block w-8 h-8 overflow-hidden rounded-full shadow-lg image-fit zoom-in intro-x"
        >
          <img :src="userStore.user?.photoURL!" />
        </Menu.Button>

        <Menu.Items
          as="div"
          class="w-56 mt-px relative bg-primary/80 before:block before:absolute before:bg-black before:inset-0 before:rounded-md before:z-[-1] text-white"
        >
          <Menu.Header class="font-normal">
            <div class="font-medium">
              {{ userStore.user?.displayName }}
            </div>
          </Menu.Header>

          <Menu.Divider class="bg-white/[0.08]" />

          <div class="font-medium ml-2">
            {{ mailAMostrar(userStore.user?.email ?? "") }}
          </div>

          <!-- <Menu.Divider class="bg-white/[0.08]" />

          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="User" class="w-4 h-4 mr-2" /> Perfil
          </Menu.Item>

          <Menu.Item class="hover:bg-white/5">
            <Lucide icon="Edit" class="w-4 h-4 mr-2" /> Mi cuenta
          </Menu.Item> -->

          <Menu.Divider class="bg-white/[0.08]" />

          <Menu.Item class="hover:bg-white/5" @click="logoutHandler">
            <Lucide icon="ToggleRight" class="w-4 h-4 mr-2" />
            Cerrar sesión
          </Menu.Item>
        </Menu.Items>
      </Menu>
    </div>
  </div>
</template>
